import React, { Fragment } from 'react';
import { ArrayInput, BooleanInput, FormDataConsumer, SelectInput, SimpleFormIterator, TimeInput, required } from 'react-admin';
import { useWatch } from 'react-hook-form';
import { Alert, Box, Grid, Typography } from '@mui/material';
import DateTimeInputUTC from '../../../components/DateTimeInputUTC';
import { daysOfTheWeek } from '../../../constants';
import { GiftProductType, GiftRuleType } from '../../../models';
import dayjs from 'dayjs';

// Frequency choices without 'never' option
const frequencyChoices = [
  { id: 'daily', name: 'Diária' },
  { id: 'weekly', name: 'Semanal' },
];

const dailyContainerSx = {
  display: 'flex',
  gap: '30px',
  marginBottom: '20px'
};

const dateTimePickerSx = {
  display: 'flex',
  gap: '20px',
  marginBottom: '20px',
};

const inputWrapperSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  margin: '0 0 20px 0',
};

const inputSx = {
  margin: '0 20px 0 0',
};

const daySx = {
  fontSize: 16,
  margin: '20px 0'
};

const FormSchedule = () => {
  const giftRules = useWatch({ name: 'giftRules' }) || [];
  const products = useWatch({ name: 'products' }) || [];
  const hasUniqueCode = Array.isArray(giftRules) && giftRules.some((r: any) => r?.type === GiftRuleType.unique_code);
  const hasPredictionTicket = Array.isArray(products) && products.some((p: any) => p?.type === GiftProductType.PredictionTicket);
  const hasPredictionWinner = Array.isArray(giftRules) && giftRules.some((r: any) => r?.type === GiftRuleType.prediction_winner);
  const disableFrequency = hasUniqueCode || hasPredictionTicket || hasPredictionWinner;

  return (
    <>
      <Box mb={3}>
        <Typography variant="h6" fontWeight={600} mb={1}>
          Agendamento do Brinde
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={0}>
          Configure o período em que o brinde estará disponível para os clientes conquistarem.
          Define quando o brinde começa e termina de estar ativo no sistema.
        </Typography>
      </Box>

      {disableFrequency && (
        <Alert severity="info" sx={{ mb: 2, maxWidth: 800, bgcolor: hasUniqueCode ? '#e3f2fd' : '#ede9fe', border: `1px solid ${hasUniqueCode ? '#2196f3' : '#7869DF'}` }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {hasUniqueCode
              ? '🎟️ Brinde de Divulgação — sem frequência'
              : '⚽ Brinde de Bolão — sem frequência'}
          </Typography>
          <Typography variant="body2">
            {hasUniqueCode
              ? 'Brindes com regra de Divulgação não se repetem (1 resgate por cliente). A frequência fica fixa em "não se repete" — controle a vigência pelas datas de início e término.'
              : 'Brindes vinculados a um bolão não se repetem (1 ticket/prêmio por cliente por bolão). A vigência casa com o prazo de palpite e o processamento do bolão — não há frequência configurável.'}
          </Typography>
        </Alert>
      )}

      {/* Data de início é sempre obrigatória e preenchida automaticamente */}
      <Grid container spacing={2} sx={{ maxWidth: 800, mb: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimeInputUTC
            source="startAt"
            label="Data e hora de início"
            validate={[required()]}
            defaultValue={dayjs().toISOString()}
            helperText="Quando o brinde começa a ficar disponível"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimeInputUTC
            source="endAt"
            label="Data e hora de término"
            helperText="Quando o brinde para de ficar disponível (opcional)"
            fullWidth
          />
        </Grid>
      </Grid>

      {/* Novo booleano para ativar frequência */}
      <BooleanInput
        source="hasFrequency"
        label="Ativar frequência"
        defaultValue={false}
        disabled={disableFrequency}
        helperText={disableFrequency
          ? 'Frequência desabilitada para este tipo de brinde'
          : 'Ative para definir uma frequência de disponibilidade (diária ou semanal)'}
      />

      <FormDataConsumer>
        {({ formData }) => (
          <Grid container spacing={2} sx={{ maxWidth: 800 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SelectInput
                label="Frequência de disponibilidade"
                source="frequency"
                alwaysOn
                choices={frequencyChoices}
                disabled={!formData.hasFrequency || disableFrequency}
                validate={formData.hasFrequency && !disableFrequency ? [required()] : []}
                helperText="Com que frequência o brinde fica disponível"
                fullWidth
              />
            </Grid>
          </Grid>
        )}
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) => {

          if (!formData.hasFrequency || disableFrequency) {
            return <></>;
          }

          if (formData.frequency === 'daily') {
            return (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Defina o horário em que o brinde fica disponível todos os dias no período agendado:
                </Typography>
                <Grid container spacing={2} sx={{ maxWidth: 800 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TimeInput
                      source="startTime"
                      label="Horário de início"
                      isRequired
                      validate={[required()]}
                      helperText="Hora que abre"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TimeInput
                      source="endTime"
                      label="Horário de término"
                      isRequired
                      validate={[required()]}
                      helperText="Hora que fecha"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>
            );
          };

          if (formData.frequency === 'weekly') {
            return (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Configure os dias da semana e horários específicos em que o brinde fica disponível:
                </Typography>
                <ArrayInput source="weekSchedual" label="Dias e horários da semana">
                  <SimpleFormIterator disableAdd disableRemove disableReordering >
                  <FormDataConsumer>
                    {({ scopedFormData }) => {
                      if (!scopedFormData) { return <></>; }

                      const day = daysOfTheWeek.find(wd => wd.id === scopedFormData.dayOfTheWeek);
                      return (
                        <>
                          <Typography sx={daySx}>{day?.name}</Typography>
                          <Box sx={inputWrapperSx}>
                            <BooleanInput
                              source="isActive"
                              label="Ativo"
                              sx={inputSx}
                            />
                            {scopedFormData && scopedFormData.isActive &&
                              <Fragment>
                                <TimeInput
                                  label='Horário de início'
                                  source="startTime"
                                  style={{ marginRight: 20, width: 200 }}
                                  isRequired
                                  validate={[required()]}
                                  helperText="Hora que abre"
                                />
                                <TimeInput
                                  label='Horário de término'
                                  source="endTime"
                                  style={{ marginRight: 20, width: 200 }}
                                  isRequired
                                  validate={[required()]}
                                  helperText="Hora que fecha"
                                />
                              </Fragment>
                            }
                          </Box>
                        </>
                      );
                    }}
                  </FormDataConsumer>
                </SimpleFormIterator>
              </ArrayInput>
              </Box>
            );
          }
        }}
      </FormDataConsumer>
    </>
  );
};

export default React.memo(FormSchedule);
